import React from 'react';

import MediaQuery from 'react-responsive';

import styled from 'styled-components';
import CtaText from 'components/ui/CtaCard';
import Select from 'components/ui/SelectField';

import { getTranslatedString } from 'helpers/i18n';
import { formatMoney } from 'helpers/Formatter';
import { colors } from 'helpers/ColorHelper';

const StyledPurchaseOrderTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
  background-color: white;
  border-radius: 12px;
  margin-bottom: 32px;
`;

const StyledPurchaseOrderTableHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const StyledPurchaseOrderTableBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledPurchaseOrderTableRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 10px 0px;
  width: 100%;
  border-bottom: ${props => (props.underLine ? `1px solid ${colors.OFF_WHITE}` : 'none')};
  padding-bottom: 10px;
`;

const StyledPurchaseOrderTableCell = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${props => props.width || '20%'};
  text-align: ${props => props.textAlign || 'left'};
  color: ${props => props.color || 'black'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledNameDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledName = styled.h5`
  margin: 0;
  font-size: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledDescription = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StlyedMobileLabel = styled.div`
  color: ${colors.FONT_GRAY};
`;

const ModifiedIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${colors.STATE_BLUE};
  position: absolute;
  right: 12px;
  top: 13px;
`;

const ModifiedIndicatorContainer = styled.div`
  position: relative;
`;

const StyledRowItemLabel = styled.div`
  margin-bottom: 5px;
  color: ${colors.FONT_GRAY};
`;

export const ViewPurchaseOrderItemsList = ({
  purchaseOrderItems,
  readOnly,
  handleOpeningEditPurchaseOrder,
  purchaseOrderItemsModified,
  glAccounts,
  handleGlAccountChange,
  purchaseOrderStatus,
}) => {
  const purchaseOrderApproved = purchaseOrderStatus === 'approved';
  const purchaseOrderPendingApproval = purchaseOrderStatus === 'pending_approval';
  return (
    <div>
      <MediaQuery query="(min-device-width: 750px)">
        <StyledPurchaseOrderTableContainer>
          <StyledPurchaseOrderTableHeader>
            <StyledPurchaseOrderTableCell
              color={colors.FONT_GRAY}
              width={purchaseOrderApproved ? '30%' : '43%'}
            >
              {getTranslatedString('landlordPortal.purchaseOrderItemList.itemUpcase')}
            </StyledPurchaseOrderTableCell>
            {purchaseOrderApproved && (
              <StyledPurchaseOrderTableCell color={colors.FONT_GRAY} width="13%" textAlign="right">
                {getTranslatedString('landlordPortal.purchaseOrderItemList.glCodeUpcase')}
              </StyledPurchaseOrderTableCell>
            )}
            <StyledPurchaseOrderTableCell color={colors.FONT_GRAY} width="7%" textAlign="right">
              {getTranslatedString('landlordPortal.purchaseOrderItemList.unitUpcase')}
            </StyledPurchaseOrderTableCell>
            <StyledPurchaseOrderTableCell color={colors.FONT_GRAY} width="15%" textAlign="right">
              {getTranslatedString('landlordPortal.purchaseOrderItemList.itemCostUpcase')}
            </StyledPurchaseOrderTableCell>
            <StyledPurchaseOrderTableCell color={colors.FONT_GRAY} width="10%" textAlign="right">
              {getTranslatedString('landlordPortal.purchaseOrderItemList.qtyUpcase')}
            </StyledPurchaseOrderTableCell>
            <StyledPurchaseOrderTableCell color={colors.FONT_GRAY} width="15%" textAlign="right">
              {getTranslatedString('landlordPortal.purchaseOrderItemList.totalUpcase')}
            </StyledPurchaseOrderTableCell>
          </StyledPurchaseOrderTableHeader>
          <StyledPurchaseOrderTableBody>
            {purchaseOrderItems.map((purchaseOrderItem, index) => (
              <React.Fragment>
                <StyledPurchaseOrderTableRow underLine={!purchaseOrderApproved}>
                  {purchaseOrderItemsModified &&
                    ['modified', 'newly_added'].includes(purchaseOrderItem.state) && (
                      <ModifiedIndicatorContainer>
                        <ModifiedIndicator />
                      </ModifiedIndicatorContainer>
                    )}
                  <StyledPurchaseOrderTableCell width={purchaseOrderApproved ? '30%' : '43%'}>
                    <StyledNameDescriptionContainer>
                      <StyledName>{purchaseOrderItem.item_name}</StyledName>
                      <StyledDescription>
                        {purchaseOrderItem.item_number
                          ? purchaseOrderItem.item_number
                          : purchaseOrderItem.item_description}
                      </StyledDescription>
                    </StyledNameDescriptionContainer>
                  </StyledPurchaseOrderTableCell>
                  {purchaseOrderApproved && (
                    <StyledPurchaseOrderTableCell width="13%" textAlign="right">
                      {purchaseOrderItem.gl_account_code?.slice(0, 7)}
                    </StyledPurchaseOrderTableCell>
                  )}
                  <StyledPurchaseOrderTableCell width="7%" textAlign="right">
                    {purchaseOrderItem.unit_number || '--'}
                  </StyledPurchaseOrderTableCell>
                  <StyledPurchaseOrderTableCell width="15%" textAlign="right">
                    {formatMoney(purchaseOrderItem.estimated_cost)}
                  </StyledPurchaseOrderTableCell>
                  <StyledPurchaseOrderTableCell width="10%" textAlign="right">
                    {purchaseOrderItem.quantity}
                  </StyledPurchaseOrderTableCell>
                  <StyledPurchaseOrderTableCell width="15%" textAlign="right">
                    {formatMoney(purchaseOrderItem.total_cost || 0)}
                  </StyledPurchaseOrderTableCell>
                </StyledPurchaseOrderTableRow>
                {purchaseOrderPendingApproval && (
                  <StyledPurchaseOrderTableRow underLine>
                    <StyledPurchaseOrderTableCell width="100%">
                      <StyledRowItemLabel>
                        {getTranslatedString(
                          'landlordPortal.approverViewPurchaseOrder.generalLedgerCode'
                        )}
                      </StyledRowItemLabel>
                      <Select
                        name="general_ledger_code"
                        showSearch
                        placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
                        size="large"
                        value={purchaseOrderItem.gl_account_id}
                        onChange={value => handleGlAccountChange(value, index)}
                        filterOption={(input, option) =>
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                      >
                        {glAccounts &&
                          glAccounts.map((glAccount, i) => (
                            <Select.Option value={glAccount.hmy} key={`${glAccount.hmy}_${i}`}>
                              {`${glAccount.description} - ${glAccount.gl_account}`}
                            </Select.Option>
                          ))}
                      </Select>
                    </StyledPurchaseOrderTableCell>
                  </StyledPurchaseOrderTableRow>
                )}
              </React.Fragment>
            ))}
          </StyledPurchaseOrderTableBody>
          {!readOnly && (
            <CtaText type="button" onClick={handleOpeningEditPurchaseOrder}>
              {getTranslatedString('landlordPortal.viewPurchaseOrderDrawer.editRequest')}
            </CtaText>
          )}
        </StyledPurchaseOrderTableContainer>
      </MediaQuery>

      <MediaQuery query="(max-device-width: 750px)">
        {purchaseOrderItems.map((purchaseOrderItem, index) => (
          <StyledPurchaseOrderTableContainer>
            <StyledPurchaseOrderTableRow underLine>
              {purchaseOrderItemsModified &&
                ['modified', 'newly_added'].includes(purchaseOrderItem.state) && (
                  <ModifiedIndicatorContainer>
                    <ModifiedIndicator />
                  </ModifiedIndicatorContainer>
                )}
              <StyledPurchaseOrderTableCell width="100%">
                <StyledName>{purchaseOrderItem.item_name}</StyledName>
                <StyledDescription>{purchaseOrderItem.item_description}</StyledDescription>
              </StyledPurchaseOrderTableCell>
            </StyledPurchaseOrderTableRow>

            <StyledPurchaseOrderTableRow underLine>
              <StyledPurchaseOrderTableCell width="50%">
                <StlyedMobileLabel>
                  {getTranslatedString('landlordPortal.purchaseOrderItemList.unitUpcase')}
                </StlyedMobileLabel>
                <div>{purchaseOrderItem.unit_number || '--'}</div>
              </StyledPurchaseOrderTableCell>
              <StyledPurchaseOrderTableCell width="50%" textAlign="right">
                <StlyedMobileLabel>
                  {getTranslatedString('landlordPortal.purchaseOrderItemList.itemCostUpcase')}
                </StlyedMobileLabel>
                <div>{formatMoney(purchaseOrderItem.estimated_cost)}</div>
              </StyledPurchaseOrderTableCell>
            </StyledPurchaseOrderTableRow>
            <StyledPurchaseOrderTableRow underLine={purchaseOrderApproved}>
              <StyledPurchaseOrderTableCell width="50%">
                <StlyedMobileLabel>
                  {getTranslatedString('landlordPortal.purchaseOrderItemList.qtyUpcase')}
                </StlyedMobileLabel>
                <div>{purchaseOrderItem.quantity}</div>
              </StyledPurchaseOrderTableCell>
              <StyledPurchaseOrderTableCell width="50%" textAlign="right">
                <StlyedMobileLabel>
                  {getTranslatedString('landlordPortal.purchaseOrderItemList.totalUpcase')}
                </StlyedMobileLabel>
                <div>{formatMoney(purchaseOrderItem.total_cost)}</div>
              </StyledPurchaseOrderTableCell>
            </StyledPurchaseOrderTableRow>
            {purchaseOrderApproved && (
              <StyledPurchaseOrderTableRow>
                <StyledPurchaseOrderTableCell width="100%">
                  <StlyedMobileLabel>
                    {getTranslatedString('landlordPortal.purchaseOrderItemList.glCodeUpcase')}
                  </StlyedMobileLabel>
                  <div>{purchaseOrderItem.gl_account_code?.slice(0, 7)}</div>
                </StyledPurchaseOrderTableCell>
              </StyledPurchaseOrderTableRow>
            )}

            {purchaseOrderPendingApproval && (
              <StyledPurchaseOrderTableRow>
                <StyledPurchaseOrderTableCell width="100%">
                  <StyledRowItemLabel>
                    {getTranslatedString(
                      'landlordPortal.approverViewPurchaseOrder.generalLedgerCode'
                    )}
                  </StyledRowItemLabel>
                  <Select
                    name="general_ledger_code"
                    showSearch
                    placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
                    size="large"
                    value={purchaseOrderItem.gl_account_id}
                    onChange={value => handleGlAccountChange(value, index)}
                    filterOption={(input, option) =>
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  >
                    {glAccounts &&
                      glAccounts.map((glAccount, i) => (
                        <Select.Option value={glAccount.hmy} key={`${glAccount.hmy}_${i}`}>
                          {`${glAccount.description} - ${glAccount.gl_account}`}
                        </Select.Option>
                      ))}
                  </Select>
                </StyledPurchaseOrderTableCell>
              </StyledPurchaseOrderTableRow>
            )}
            <StyledPurchaseOrderTableCell width="100%">
              {!readOnly && (
                <CtaText type="button" onClick={handleOpeningEditPurchaseOrder}>
                  {getTranslatedString('landlordPortal.viewPurchaseOrderDrawer.editRequest')}
                </CtaText>
              )}
            </StyledPurchaseOrderTableCell>
          </StyledPurchaseOrderTableContainer>
        ))}
      </MediaQuery>
    </div>
  );
};
